import React, { useEffect, useRef, useState } from "react";
import embed from "vega-embed";
import { getFullscreenElement, requestElementFullscreen, exitNativeFullscreen } from "../utils/fullscreen";
import { getAllowedOrigins, validateSpecUrls, applyReadabilityDefaults } from "../utils/vegaChartUtils";

export default function VegaChart({ spec }) {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPseudoFullscreen, setIsPseudoFullscreen] = useState(false);
  const isExpanded = isFullscreen || isPseudoFullscreen;

  useEffect(() => {
    const onFullScreenChange = () => {
      const wrapperEl = wrapperRef.current;
      const fsElement = getFullscreenElement();
      const nativeFullscreen = Boolean(wrapperEl && fsElement === wrapperEl);
      setIsFullscreen(nativeFullscreen);
      if (nativeFullscreen) {
        setIsPseudoFullscreen(false);
      }
    };

    document.addEventListener("fullscreenchange", onFullScreenChange);
    document.addEventListener("webkitfullscreenchange", onFullScreenChange);
    document.addEventListener("MSFullscreenChange", onFullScreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFullScreenChange);
      document.removeEventListener("webkitfullscreenchange", onFullScreenChange);
      document.removeEventListener("MSFullscreenChange", onFullScreenChange);
    };
  }, []);

  useEffect(() => {
    if (!isPseudoFullscreen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isPseudoFullscreen]);

  const toggleFullscreen = async () => {
    const wrapperEl = wrapperRef.current;
    if (!wrapperEl) return;

    try {
      const fsElement = getFullscreenElement();
      const hasNativeForThisChart = fsElement === wrapperEl;

      if (hasNativeForThisChart || isPseudoFullscreen) {
        if (hasNativeForThisChart) {
          await exitNativeFullscreen();
        }
        setIsPseudoFullscreen(false);
        setIsFullscreen(false);
        return;
      }

      const usedNative = await requestElementFullscreen(wrapperEl);
      if (!usedNative) {
        setIsPseudoFullscreen(true);
      }
    } catch (_err) {
      // Fallback for mobile/webviews that reject native fullscreen.
      setIsPseudoFullscreen(true);
    }
  };

  useEffect(() => {
    if (!containerRef.current || !spec) return;
    let view = null;
    let isDisposed = false;
    const container = containerRef.current;

    // Clear stale embeds before rendering a new chart.
    container.innerHTML = "";

    const currentOrigin = window?.location?.origin || "http://localhost";
    const allowedOrigins = getAllowedOrigins();
    const specUrlIssue = validateSpecUrls(spec, allowedOrigins, currentOrigin);
    if (specUrlIssue) {
      container.textContent = `Vega-Lite security error: ${specUrlIssue}`;
      return undefined;
    }

    // Default to safer layout settings; avoid forcing `fit` globally because
    // many generated specs (facet/concat/repeat) fail under strict fit sizing.
    const resolvedSpec = applyReadabilityDefaults({
      ...spec,
      width: spec?.width ?? "container",
      height: spec?.height ?? 350,
      background: spec?.background ?? "#f5f5f5",
      autosize: spec?.autosize ?? { type: "pad", contains: "padding" },
    });

    const fullscreenHeight = Math.max((window?.innerHeight || 900) - 140, 420);
    const resolvedSpecForMode = isExpanded
      ? {
          ...resolvedSpec,
          width: "container",
          height:
            typeof resolvedSpec.height === "number"
              ? Math.max(resolvedSpec.height, fullscreenHeight)
              : fullscreenHeight,
          autosize: { type: "fit", contains: "padding" },
        }
      : resolvedSpec;

    const render = (candidateSpec) =>
      embed(container, candidateSpec, {
        actions: false,
        renderer: "svg",
        // vega-embed v6 expects an object (or false) for tooltip, not a boolean.
        tooltip: {},
      });

    const renderWithFallback = async () => {
      try {
        const result = await render(resolvedSpecForMode);
        if (isDisposed) {
          result.view.finalize();
          return;
        }
        view = result.view;
      } catch (err) {
        const fallbackSpec = {
          ...resolvedSpecForMode,
          autosize: { type: "pad", contains: "padding" },
          width:
            typeof resolvedSpecForMode.width === "number" ? resolvedSpecForMode.width : 500,
          height:
            typeof resolvedSpecForMode.height === "number" ? resolvedSpecForMode.height : 350,
        };

        try {
          container.innerHTML = "";
          const result = await render(fallbackSpec);
          if (isDisposed) {
            result.view.finalize();
            return;
          }
          view = result.view;
        } catch (fallbackErr) {
          if (!isDisposed) {
            const message = fallbackErr?.message || err?.message || "Unknown Vega-Lite error";
            container.textContent = `Vega-Lite error: ${message}`;
          }
        }
      }
    };

    void renderWithFallback();

    return () => {
      isDisposed = true;
      if (view) view.finalize();
      container.innerHTML = "";
    };
  }, [spec, isExpanded]);

  return (
    <div
      ref={wrapperRef}
      style={{
        position: isPseudoFullscreen ? "fixed" : "relative",
        inset: isPseudoFullscreen ? 0 : "auto",
        // Always set an explicit zIndex so this element forms its own stacking
        // context. Without it (zIndex:"auto"), the Fullscreen button's zIndex:3
        // is global and floats above the input card (zIndex:2) on scroll.
        zIndex: isPseudoFullscreen ? 9999 : 1,
        background: isPseudoFullscreen ? "#f5f5f5" : "transparent",
        width: "100%",
        height: isExpanded ? "100dvh" : "auto",
        margin: isExpanded ? 0 : "16px auto",
        borderRadius: isExpanded ? 0 : "8px",
      }}
    >
      <button
        type="button"
        onClick={toggleFullscreen}
        style={{
          position: "absolute",
          top: "12px",
          right: "12px",
          zIndex: 3,
          fontSize: "0.8rem",
          border: "1px solid var(--primary-color-dark, #4f46e5)",
          borderRadius: "6px",
          padding: "5px 10px",
          background: "var(--primary-color, #636cff)",
          color: "#ffffff",
          fontWeight: 600,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.18)",
          cursor: "pointer",
        }}
        aria-label={isExpanded ? "Exit fullscreen chart" : "View chart fullscreen"}
        title={isExpanded ? "Exit fullscreen" : "Fullscreen"}
      >
        {isExpanded ? "Exit fullscreen" : "Fullscreen"}
      </button>

      <div
        ref={containerRef}
        className="vega-chart-container"
        style={{
          display: "block",
          width: "100%",
          maxWidth: "100%",
          height: isExpanded ? "100%" : "auto",
          minHeight: isExpanded ? "100%" : "160px",
          overflowX: "auto",
          borderRadius: isExpanded ? 0 : "8px",
          padding: "8px",
          background: "#f5f5f5",
          color: "#1f2937",
          boxSizing: "border-box",
        }}
      />
    </div>
  );
}
